import { type FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as DOMPurify from 'dompurify';
import {
  Box,
  Collapse,
  Paper,
  Skeleton,
  Typography,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  Popover,
  Tooltip,
} from '@mui/material';
import {
  Article as ArticleIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  TextDecrease as TextDecreaseIcon,
  TextIncrease as TextIncreaseIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  StickyNote2 as NoteIcon,
  StickyNote2Outlined as NoteOutlinedIcon,
  Spellcheck as SpellcheckIcon,
} from '@mui/icons-material';
import type { CaptionFetchState } from '@/hooks/useYouTubeCaptions';
import { useYouTubeQuota } from '@/hooks/useYouTubeQuota';
import { useExplainSegment } from '@/hooks/useExplainer';
import { ExplanationPanel } from '@/components/transcripts/ExplanationPanel';
import type { useYouTubeTranscriptEdit } from '@/hooks/useYouTubeTranscriptEdit';
import { useTranslateText } from '@/hooks/useTranslation';
import { HighlightedText } from '@/components/vocabulary/HighlightedText';
import { useAddVocabularyWord, useLinkExistingVocabulary } from '@/hooks/useVocabulary';
import toast from 'react-hot-toast';
import type { VocabularyWord } from '@/types/api.types';
import { decodeHtmlEntities } from '@/utils/textUtils';
import { ROUTES } from '@/config/constants';
import {
  studyEditorResponsiveLayoutSx,
  studyEditorResponsiveColumnSx,
} from './videoPlayerResponsiveLayout';
import { NoteModal } from '@/components/notes/NoteModal';

interface VideoPlayerTranscriptPanelProps {
  /** The currently selected segment, or null if none is active. */
  segment: {
    id: number;
    start_time: number;
    end_time: number;
    hasTranscriptNote?: boolean;
  } | null;
  /** 1-based display index of the segment within the video's segment list. */
  segmentIndex: number | null;
  /**
   * Current caption fetch lifecycle state from useYouTubeCaptions.
   * Defaults to { status: 'idle' } when not provided.
   */
  captionState?: CaptionFetchState;
  /**
   * Called when the user clicks "Get Transcript" or after a revert completes.
   * Caption fetch is strictly user-initiated — never auto-called on render or
   * segment change (Story 3.1 AC 5). Post-revert auto-call is the exception:
   * it saves the user an extra click (preferred UX per Story 3.2 dev notes).
   */
  onGetTranscript?: () => void;
  /**
   * Story 3.2: Edit state hook instance, created in VideoPlayer and passed down.
   * Follows the same prop-drilling pattern as captionState / onGetTranscript —
   * do NOT create the hook inside this panel.
   */
  editHook?: ReturnType<typeof useYouTubeTranscriptEdit>;
  /**
   * Story 4.1: Project ID used when saving vocabulary words.
   */
  projectId?: number;
  /** Source language name/code from project (e.g., Japanese/ja). */
  projectLanguage?: string;
  /** Target language name/code from project (e.g., English/en). */
  userLanguage?: string;
  /**
   * Story 4.1: Vocabulary words already saved for the current segment,
   * fetched in VideoPlayer and passed down (prop-drilling pattern).
   * Defaults to [] if not provided.
   */
  segmentVocabWords?: VocabularyWord[];
  /**
   * Called after the user toggles from edit mode back to view mode and any
   * pending save has completed. VideoPlayer passes () => fetchFromCache(segmentId)
   * so view mode immediately shows the freshly saved text instead of the stale
   * pre-edit captionState.
   */
  onRefreshTranscript?: () => void;
  /**
   * Called after the user successfully exits edit mode (view-mode toggle).
   * VideoPlayer uses this to clear activeSegmentId so free-play segment
   * auto-progression resumes instead of staying locked to the edited segment's
   * boundary in the 500ms polling interval.
   */
  onExitEditMode?: () => void;
  /**
   * Story 2.6: Called when user enters transcript edit mode for the current segment.
   * Used to mark the segment as "worked" for Resume mode.
   */
  onSegmentWorked?: () => void;
  /**
   * True while the playhead is between segments during free playback.
   * The panel renders an animated skeleton placeholder instead of stale content or
   * the "select a segment" hint, keeping the layout height stable.
   */
  isGap?: boolean;
}

/**
 * VideoPlayerTranscriptPanel — live caption fetch + user editing surface.
 *
 * Story 3.1 scope: fetch lifecycle — idle → loading → transcript (read-only) | no-captions | error.
 * Story 3.2 scope: source-aware rendering, edit affordance, auto-save indicator, revert.
 * Story 4.1 scope: text-selection vocabulary save, HighlightedText display for saved words.
 *
 * Display priority (matches backend rule):
 *   1. source='user' → enter edit mode immediately; show "Revert to YouTube".
 *   2. source='youtube' → render as read-only; show "Edit" button.
 *   3. No transcript / idle → show "Get Transcript" call-to-action.
 *
 * Auto-save:
 *   - Fires after 5 seconds idle (managed in useYouTubeTranscriptEdit).
 *   - Indicated by inline CircularProgress(14) near the field label — no toast.
 *   - aria-live="polite" announces saving status to screen readers without interrupting typing.
 *
 * Revert:
 *   - Calls editHook.revert(segment.id) which soft-deletes the source='user' row.
 *   - After revert, calls onGetTranscript() to automatically re-fetch the youtube transcript,
 *     saving the user an extra click (preferred UX per story dev notes).
 */
const FONT_SIZE_MIN = 12;
const FONT_SIZE_MAX = 32;
const FONT_SIZE_STEP = 2;
const FONT_SIZE_DEFAULT = 16;
const LS_KEY = 'transcript-font-size';

const LANGUAGE_TO_CODE: Record<string, string> = {
  english: 'en',
  spanish: 'es',
  french: 'fr',
  german: 'de',
  italian: 'it',
  portuguese: 'pt',
  japanese: 'ja',
  chinese: 'zh',
  korean: 'ko',
  russian: 'ru',
  turkish: 'tr',
};

const toLanguageCode = (language: string) => {
  const lower = language.toLowerCase();
  if (lower.includes('-')) {
    return lower.split('-')[0];
  }
  return LANGUAGE_TO_CODE[lower] ?? lower;
};

/** DOMPurify instance — normalised across bundler/tsconfig module shapes. */
const purifier: { sanitize: (s: string, opt?: Record<string, unknown>) => string } =
  ((DOMPurify as unknown as { default?: unknown }).default ?? DOMPurify) as never;

const RUBY_TAGS = {
  ALLOWED_TAGS: ['ruby', 'rt', 'rb', 'br', 'b', 'i', 'em', 'strong', 'span', 'div', 'p'],
  ALLOWED_ATTR: ['class'],
};

/**
 * Renders transcript text. When the text is furigana ruby HTML (contains '<ruby'),
 * it is sanitised with DOMPurify and rendered via dangerouslySetInnerHTML so the
 * browser displays the ruby annotations (furigana above kanji). Otherwise the text
 * is rendered as a plain Typography node.
 */
function renderTranscriptText(
  text: string,
  altText: string | undefined,
  fontSize: number,
  testId: string,
) {
  const display = altText ?? text;
  if (display.includes('<ruby')) {
    return (
      <div
        data-testid={testId}
        style={{ whiteSpace: 'pre-wrap', lineHeight: 1.9, fontSize }}
        dangerouslySetInnerHTML={{ __html: purifier.sanitize(display, RUBY_TAGS) }}
      />
    );
  }
  return (
    <span
      data-testid={testId}
      style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, fontSize, display: 'block' }}
    >
      {text}
    </span>
  );
}

export const VideoPlayerTranscriptPanel: FC<VideoPlayerTranscriptPanelProps> = ({
  segment,
  segmentIndex: _segmentIndex,
  captionState = { status: 'idle' },
  onGetTranscript,
  editHook,
  projectId,
  projectLanguage,
  userLanguage,
  segmentVocabWords = [],
  onRefreshTranscript,
  onExitEditMode,
  onSegmentWorked,
  isGap = false,
}) => {
  const { t } = useTranslation('player');
  const { t: tSegments } = useTranslation('segments');
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [refreshSnapshot, setRefreshSnapshot] = useState<CaptionFetchState | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Tracks whether the in-flight refresh has actually been observed reaching
  // captionState.status === 'loading' yet — see the resolve-effect below for why.
  const [sawRefreshLoading, setSawRefreshLoading] = useState(false);

  const [fontSize, setFontSize] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      if (stored) {
        const n = parseInt(stored, 10);
        if (n >= FONT_SIZE_MIN && n <= FONT_SIZE_MAX) return n;
      }
    } catch { /* ignore SSR / sandboxed environments */ }
    return FONT_SIZE_DEFAULT;
  });

  // Story 4.1: vocabulary save state
  const [selectedWord, setSelectedWord] = useState('');
  const [savePopoverOpen, setSavePopoverOpen] = useState(false);
  // anchorPosition uses selection rect (Task 2.3) instead of element ref
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [manualTranslation, setManualTranslation] = useState('');
  const [vocabSaveSuccess, setVocabSaveSuccess] = useState(false);

  const addWordMutation = useAddVocabularyWord();
  const linkExistingMutation = useLinkExistingVocabulary();
  const translateText = useTranslateText();
  const { data: quotaData, isLoading: quotaLoading } = useYouTubeQuota();

  const {
    isOpen: explainOpen,
    isLoading: explainLoading,
    explanation,
    error: explainError,
    explain,
    dismiss: dismissExplain,
    resetForSegment,
  } = useExplainSegment(segment?.id ?? 0);

  // Story 4.1: clear selection state when segment changes (Task 2.4)
  useEffect(() => {
    setSelectedWord('');
    setSavePopoverOpen(false);
    addWordMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id]);

  // Discard any stale refresh snapshot when the selected segment changes so a
  // refresh started on a previous segment can't bleed into the new one.
  useEffect(() => {
    setRefreshSnapshot(null);
    setIsRefreshing(false);
    setSawRefreshLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id]);

  // Clear the refresh snapshot once the triggered fetch resolves (success,
  // error, no-captions, etc.) so the panel falls back to rendering
  // captionState normally instead of the stale pre-refresh snapshot.
  //
  // Guarded on captionState.status === 'loading' (not "!== 'loading'") because
  // handleRefreshClick sets isRefreshing=true synchronously in the same tick it
  // calls onGetTranscript(); captionState is still 'success' from the *previous*
  // fetch at that render, and onGetTranscript's loading update from the parent
  // hasn't landed yet. Firing on "status !== loading" would immediately clear
  // the just-set isRefreshing flag before the parent ever flips to loading.
  // Instead, wait for the parent to actually enter 'loading' first, then treat
  // any subsequent non-loading status as the resolved fetch.
  useEffect(() => {
    if (isRefreshing && captionState.status === 'loading') {
      setSawRefreshLoading(true);
    } else if (isRefreshing && sawRefreshLoading && captionState.status !== 'loading') {
      setIsRefreshing(false);
      setRefreshSnapshot(null);
      setSawRefreshLoading(false);
    }
  }, [isRefreshing, sawRefreshLoading, captionState.status]);

  // Reset explainer state when segment changes
  useEffect(() => {
    resetForSegment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id]);

  // Story 4.1: show inline success icon for 2 seconds after save (Task 2.6)
  useEffect(() => {
    if (addWordMutation.isSuccess) {
      setSavePopoverOpen(false);
      setSelectedWord('');
      setVocabSaveSuccess(true);
      const timer = setTimeout(() => setVocabSaveSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [addWordMutation.isSuccess]);

  // Story 4.1: close popover automatically on save error so user can retry (Task 2.7)
  useEffect(() => {
    if (addWordMutation.isError) {
      setSavePopoverOpen(false);
      setSelectedWord('');
      addWordMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addWordMutation.isError]);

  // Always-editable: when the selected segment has no transcript at all (idle —
  // nothing cached), auto-open an empty edit textarea instead of the placeholder
  // + buttons, mirroring the podcast editor. Not a user action: never calls
  // onSegmentWorked, never pins the segment, never triggers a fetch.
  useEffect(() => {
    if (!segment || isGap || !editHook || editHook.editMode) return;
    if (captionState.status === 'idle') {
      editHook.enterEditMode(segment.id, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment?.id, isGap, captionState.status, editHook?.editMode]);

  const changeFontSize = (delta: number) => {
    setFontSize((prev) => {
      const next = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, prev + delta));
      try { localStorage.setItem(LS_KEY, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const isEditing = editHook?.editMode ?? false;
  const isTwoColumnMode = isEditing || captionState.status === 'success';

  // Current transcript text shown to the user, used to prepopulate a new note.
  // Prefer the live edit buffer while editing; otherwise fall back to the
  // currently displayed (successfully fetched) transcript text.
  const noteSourceText = isEditing
    ? (editHook?.editText ?? '')
    : captionState.status === 'success'
      ? captionState.transcript
      : '';

  // Story 4.1: text selection handler for vocabulary save (Task 2.3)
  // Works in both view mode (window.getSelection) and edit mode (textarea selectionStart/End).
  const handleTextSelection = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    // window.getSelection() works for read-only divs but not for textarea elements.
    // For textarea (edit mode), fall back to selectionStart/selectionEnd on the target.
    const selection = window.getSelection();
    let selectionText = selection?.toString().trim() ?? '';

    if (!selectionText) {
      const target = event.target as HTMLTextAreaElement;
      if (typeof target.selectionStart === 'number' && typeof target.selectionEnd === 'number') {
        selectionText = (target.value ?? '').substring(target.selectionStart, target.selectionEnd).trim();
      }
    }

    if (selectionText) {
      setSelectedWord(selectionText);
      setManualTranslation('');
      addWordMutation.reset();

      // Anchor near the selection rect; for textarea selections fall back to mouse position
      let positioned = false;
      if (selection?.rangeCount && selection.rangeCount > 0) {
        const rect = selection.getRangeAt(0).getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) {
          setAnchorPosition({ top: rect.bottom, left: rect.left });
          positioned = true;
        }
      }
      if (!positioned) {
        if ('clientY' in event) {
          setAnchorPosition({ top: event.clientY + 8, left: event.clientX });
        } else {
          const containerRect = event.currentTarget.getBoundingClientRect();
          setAnchorPosition({ top: containerRect.bottom, left: containerRect.left });
        }
      }
      setSavePopoverOpen(true);
    }
  };

  const handlePopoverClose = () => {
    setSavePopoverOpen(false);
    setSelectedWord('');
    setManualTranslation('');
    addWordMutation.reset();
  };

  const handleTranslateSelectedWord = () => {
    if (!selectedWord || !projectLanguage || !userLanguage) return;

    translateText.mutate(
      {
        text: selectedWord,
        sourceLanguage: toLanguageCode(projectLanguage),
        targetLanguage: toLanguageCode(userLanguage),
      },
      {
        onSuccess: (data) => {
          setManualTranslation(decodeHtmlEntities(data.translatedText));
        },
      },
    );
  };

  const handleMarkVocabulary = () => {
    if (!segment || projectId == null || !noteSourceText.trim()) return;
    linkExistingMutation.mutate(
      { segmentId: segment.id, projectId, sourceType: 'transcript' },
      {
        onSuccess: (result) => {
          if (result.linked > 0) {
            toast.success(`Marked ${result.linked} vocabulary word${result.linked === 1 ? '' : 's'}`);
          } else if (result.matched > 0) {
            toast.success('Your vocabulary words here are already marked');
          } else {
            toast('No saved vocabulary words found in this transcript');
          }
        },
      },
    );
  };

  const handleEditClick = () => {
    if (!segment || captionState.status !== 'success') return;
    // When editing a youtube-sourced transcript, open an EMPTY textarea so the
    // user writes their own transcription. The YouTube text remains visible as a
    // read-only reference panel in renderEditArea. Pre-seeding would conflate
    // the two sources and put YouTube content into the user's editable field.
    const seedText = captionState.source === 'user' ? captionState.transcript : '';
    editHook?.enterEditMode(segment.id, seedText);
    onSegmentWorked?.();
  };

  /**
   * Toggle from edit mode back to view mode.
   *
   * Two bugs this prevents:
   * 1. Data loss: the old code called exitEditMode() directly, which cancelled the
   *    5-second auto-save debounce without saving. Any text typed within the last
   *    5 seconds was silently discarded.
   * 2. Visual reversion: even when auto-save had already fired, captionState still
   *    held the pre-edit text from the last fetchFromCache. Exiting edit mode would
   *    render that stale text, making the user think their changes were lost even
   *    though the DB had the correct new value.
   *
   * Fix: save any unsaved changes first, stay in edit mode on error so the user
   * can retry, then exit and trigger a cache re-fetch so view mode shows the
   * freshly saved text.
   */
  const handleViewModeClick = async () => {
    if (!editHook) return;
    // Save any changes that haven't been persisted yet (either because auto-save
    // hasn't fired yet or because this is the first edit). saveNow cancels any
    // pending debounce timer before saving, so no duplicate requests.
    if (editHook.hasUnsavedChanges) {
      const saved = await editHook.saveNow();
      if (!saved) {
        // Save failed — stay in edit mode so the user sees the error and can retry.
        return;
      }
    }
    editHook.exitEditMode();
    // Re-fetch from DB cache so view mode shows the text we just saved rather than
    // the stale captionState from when the segment was first selected.
    onRefreshTranscript?.();
    // Release the segment boundary lock so free-play auto-progression resumes.
    // Without this, activeSegmentId would remain set and the 500ms polling
    // interval in YouTubePlayer would keep pausing at the segment's end_time.
    onExitEditMode?.();
  };

  const handleRefreshClick = () => {
    if (captionState.status !== 'success' || !onGetTranscript) return;
    setRefreshSnapshot(captionState);
    setIsRefreshing(true);
    onGetTranscript();
  };

  const handleWriteFromScratch = () => {
    if (!segment) return;
    editHook?.enterEditMode(segment.id, '');
    onSegmentWorked?.();
  };

  const renderSaveIndicator = () => {
    if (!editHook) return null;
    if (editHook.saveState === 'saving') {
      return (
        <CircularProgress
          size={14}
          sx={{ ml: 0.75 }}
          aria-hidden="true"
          data-testid="transcript-save-spinner"
        />
      );
    }
    if (editHook.saveState === 'saved') {
      return (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, ml: 0.75, color: 'success.main' }}
          aria-hidden="true"
          data-testid="transcript-save-check"
        />
      );
    }
    return null;
  };

  const renderLeftColumnHeader = () => (
    <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
      <Typography variant="subtitle2" fontWeight="bold">
        {t('transcriptEditLabel')}
      </Typography>
      {(isEditing || captionState.status === 'success') && (
        <Tooltip title={isEditing ? t('switchToViewMode', 'Switch to view mode') : t('switchToEditMode', 'Switch to edit mode')}>
          <IconButton
            size="small"
            onClick={isEditing ? handleViewModeClick : handleEditClick}
            color={isEditing ? 'primary' : 'default'}
            data-testid="transcript-edit-btn"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            {isEditing ? <VisibilityIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      {isEditing && renderSaveIndicator()}
      {isEditing && editHook?.saveState === 'error' && (
        <Typography
          variant="caption"
          color="error"
          sx={{ ml: 0.75 }}
          data-testid="transcript-save-error"
        >
          ✕
        </Typography>
      )}
      {vocabSaveSuccess && (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, color: 'success.main', ml: 0.5 }}
          data-testid="vocab-save-success-icon"
        />
      )}
      {(isEditing || captionState.status === 'success') && (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 'auto', flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => changeFontSize(-FONT_SIZE_STEP)}
            disabled={fontSize <= FONT_SIZE_MIN}
            aria-label={t('decreaseFontSize')}
            data-testid="transcript-font-decrease"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <TextDecreaseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 32, textAlign: 'center', userSelect: 'none' }}
            data-testid="transcript-font-size-label"
          >
            {fontSize}px
          </Typography>
          <IconButton
            size="small"
            onClick={() => changeFontSize(FONT_SIZE_STEP)}
            disabled={fontSize >= FONT_SIZE_MAX}
            aria-label={t('increaseFontSize')}
            data-testid="transcript-font-increase"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <TextIncreaseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
      {segment && noteSourceText.trim() && projectId != null && (
        <Tooltip title="Mark my vocabulary words in this transcript">
          <span>
            <IconButton
              size="small"
              onClick={handleMarkVocabulary}
              disabled={linkExistingMutation.isPending}
              data-testid="mark-vocabulary-btn"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              {linkExistingMutation.isPending ? <CircularProgress size={16} /> : <SpellcheckIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {segment && (
        <Tooltip title={segment.hasTranscriptNote ? tSegments('noteViewEditTooltip') : tSegments('noteAddTooltip')}>
          <IconButton
            size="small"
            onClick={() => setIsNoteModalOpen(true)}
            color={segment.hasTranscriptNote ? 'primary' : 'default'}
            data-testid="transcript-note-icon"
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            {segment.hasTranscriptNote ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );

  const renderEditArea = () => {
    // Always show the YouTube transcript as a read-only reference panel below the
    // editable textarea so the user can compare without destructive action.
    // - source='user':    captionState.transcript = user text,
    //                     captionState.youtubeTranscript = youtube cache
    // - source='youtube': captionState.transcript IS the youtube content;
    //                     youtubeTranscript is undefined (no separate user row)
    const youtubeComparison =
      captionState.status === 'success'
        ? captionState.source === 'user'
          ? captionState.youtubeTranscript
          : captionState.transcript
        : undefined;
    // altTranscript always represents the furigana-annotated YouTube text.
    // It applies to youtubeComparison regardless of source.
    const altYoutubeText =
      captionState.status === 'success' ? captionState.altTranscript : undefined;

    return (
      <Box sx={studyEditorResponsiveLayoutSx} data-testid="transcript-edit-layout">
        <Stack sx={studyEditorResponsiveColumnSx} spacing={0.5}>
          {renderLeftColumnHeader()}
          <Box onMouseUp={handleTextSelection} onTouchEnd={handleTextSelection}>
            <TextField
              multiline
              minRows={3}
              fullWidth
              value={editHook?.editText ?? ''}
              onChange={(e) => editHook?.onTextChange(e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{ 'aria-label': t('transcriptEditLabel'), style: { fontSize, lineHeight: 1.7 } }}
              sx={{ bgcolor: 'white' }}
              data-testid="transcript-textarea"
            />
          </Box>

        </Stack>
        <Stack sx={studyEditorResponsiveColumnSx} spacing={0.5}>
          {youtubeComparison ? (
            /* ── YouTube version: read-only reference panel, no action buttons ── */
            <>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('youTubeVersionLabel')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 'auto', flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => changeFontSize(-FONT_SIZE_STEP)}
                    disabled={fontSize <= FONT_SIZE_MIN}
                    aria-label={t('decreaseFontSize')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <TextDecreaseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 32, textAlign: 'center', userSelect: 'none' }}
                  >
                    {fontSize}px
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => changeFontSize(FONT_SIZE_STEP)}
                    disabled={fontSize >= FONT_SIZE_MAX}
                    aria-label={t('increaseFontSize')}
                    sx={{ minWidth: 44, minHeight: 44 }}
                  >
                    <TextIncreaseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Stack>
              </Stack>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  borderLeft: 3,
                  borderColor: 'info.main',
                }}
                data-testid="transcript-youtube-comparison"
              >
                {renderTranscriptText(youtubeComparison, altYoutubeText, fontSize, 'transcript-youtube-comparison-text')}
              </Box>
            </>
          ) : captionState.status === 'loading' ? (
            /* ── Fetching YouTube transcript — show inline spinner ── */
            <Stack direction="row" alignItems="center" spacing={1} data-testid="transcript-compare-loading">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {t('transcriptLoading')}
              </Typography>
            </Stack>
          ) : onGetTranscript ? (
            /* ── No YouTube version fetched yet — offer to compare ── */
            <Button
              variant="text"
              size="small"
              startIcon={<ArticleIcon />}
              onClick={onGetTranscript}
              data-testid="transcript-compare-btn"
              sx={{ alignSelf: 'flex-start', color: 'text.secondary', minHeight: 44 }}
            >
              {t('compareWithYouTube')}
            </Button>
          ) : null}
        </Stack>
      </Box>
    );
  };

  return (
    <Box data-testid="transcript-panel" sx={{ borderTop: 1, borderColor: 'divider' }}>
      {/* Content area — minHeight keeps the panel stable while transcript loads */}
      <Box sx={{ px: 2, py: 2, minHeight: 168, bgcolor: 'grey.50' }}>
        {isGap || segment == null ? (
          isGap ? (
            /* ── Inter-segment gap: skeleton placeholder keeps height stable ───── */
            <Paper
              variant="outlined"
              sx={{ p: 1.5, bgcolor: 'white', minHeight: 120, borderRadius: 1 }}
              data-testid="transcript-gap-skeleton"
            >
              <Skeleton variant="text" sx={{ fontSize: `${fontSize}px`, width: '92%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: `${fontSize}px`, width: '78%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: `${fontSize}px`, width: '85%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: `${fontSize}px`, width: '60%', mb: 0.5 }} />
              <Skeleton variant="text" sx={{ fontSize: `${fontSize}px`, width: '72%' }} />
            </Paper>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {t('selectSegmentFirst')}
            </Typography>
          )
        ) : (
          <>
            {!isTwoColumnMode && (
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('transcriptEditLabel')}
                </Typography>
                {vocabSaveSuccess && (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 16, color: 'success.main', ml: 0.5 }}
                    data-testid="vocab-save-success-icon"
                  />
                )}
                {segment && (
                  <Tooltip title={segment.hasTranscriptNote ? tSegments('noteViewEditTooltip') : tSegments('noteAddTooltip')}>
                    <IconButton
                      size="small"
                      onClick={() => setIsNoteModalOpen(true)}
                      color={segment.hasTranscriptNote ? 'primary' : 'default'}
                      data-testid="transcript-note-icon"
                      sx={{ minWidth: 44, minHeight: 44, ml: 'auto' }}
                    >
                      {segment.hasTranscriptNote ? <NoteIcon fontSize="small" /> : <NoteOutlinedIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            {isEditing ? (
              /* ── Edit mode — hoisted above loading so the textarea stays visible
                  when the user clicks "Compare with YouTube" while editing.
                  The inline spinner inside renderEditArea handles the loading sub-state. ── */
              <Stack spacing={1.5} data-testid="transcript-content" sx={{ position: 'relative' }}>
            <Box
              component="span"
              aria-live="polite"
              aria-atomic="true"
              data-testid="transcript-aria-live"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {editHook?.saveState === 'saving'
                ? t('transcriptSaving')
                : editHook?.saveState === 'saved'
                  ? t('transcriptSaved')
                  : ''}
            </Box>
            {renderEditArea()}
          </Stack>
        ) : (captionState.status === 'success' || (isRefreshing && refreshSnapshot?.status === 'success')) ? (
          /* ── Success: source-aware rendering (Story 3.2) ────────────── */
          (() => {
            const displayState =
              isRefreshing && refreshSnapshot?.status === 'success' ? refreshSnapshot : captionState;
            if (displayState.status !== 'success') return null;
            return (
          <Stack spacing={1.5} data-testid="transcript-content" sx={{ position: 'relative' }}>
            {/* aria-live region for screen reader auto-save announcements (AC 5).
                Hidden visually but announced by screen readers via polite policy
                (never assertive — would interrupt typing per UX spec line 793/824). */}
            <Box
              component="span"
              aria-live="polite"
              aria-atomic="true"
              data-testid="transcript-aria-live"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                padding: 0,
                margin: '-1px',
                overflow: 'hidden',
                clip: 'rect(0,0,0,0)',
                whiteSpace: 'nowrap',
                border: 0,
              }}
            >
              {editHook?.saveState === 'saving'
                ? t('transcriptSaving')
                : editHook?.saveState === 'saved'
                  ? t('transcriptSaved')
                  : ''}
            </Box>

            {/* Read-only transcript display — same two-column layout as edit mode so
                the user and YouTube versions don't shift positions on edit toggle.
                The left column always shows the editable "your transcript" box
                (empty + click-to-edit when source='youtube' and no user row exists
                yet) so the editable surface is never hidden, matching TranscriptEditor. */}
            <Box sx={studyEditorResponsiveLayoutSx} data-testid="transcript-view-layout">
              {/* Left column: read-only transcript text */}
              <Stack sx={studyEditorResponsiveColumnSx} spacing={0.5}>
                {renderLeftColumnHeader()}
                <Paper
                  variant="outlined"
                  onClick={handleEditClick}
                  onMouseUp={handleTextSelection}
                  onTouchEnd={handleTextSelection}
                  sx={{
                    p: 1.5,
                    bgcolor: 'white',
                    minHeight: 120,
                    overflow: 'auto',
                    cursor: 'text',
                    borderRadius: 1,
                  }}
                  style={{ fontSize, lineHeight: 1.7 }}
                  data-testid="transcript-selectable"
                >
                  {displayState.source === 'user' ? (
                    <HighlightedText
                      text={displayState.transcript}
                      vocabularyWords={segmentVocabWords}
                      segmentId={segment.id}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary" data-testid="transcript-readonly-text">
                      {t('clickToStartTyping', 'Click to start typing...')}
                    </Typography>
                  )}
                </Paper>
              </Stack>

              {/* Right column: YouTube comparison — shown regardless of source so the
                  editable box on the left is never paired with a missing comparison. */}
              <Stack sx={studyEditorResponsiveColumnSx} spacing={0.5}>
                {(() => {
                  const youtubeComparison =
                    displayState.source === 'user'
                      ? displayState.youtubeTranscript
                      : displayState.transcript;
                  return youtubeComparison ? (
                    <>
                      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="nowrap">
                        <Typography variant="subtitle2" fontWeight="bold">
                          {t('youTubeVersionLabel')}
                        </Typography>
                        {(captionState.status === 'success' || isRefreshing) && (
                          <Tooltip title={isRefreshing ? t('refreshingTranscript') : t('refreshTranscript')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={handleRefreshClick}
                                disabled={isRefreshing}
                                data-testid="transcript-refresh-btn"
                                sx={{ minWidth: 44, minHeight: 44 }}
                              >
                                {isRefreshing ? (
                                  <CircularProgress size={16} data-testid="transcript-refresh-spinner" />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ ml: 'auto', flexShrink: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => changeFontSize(-FONT_SIZE_STEP)}
                            disabled={fontSize <= FONT_SIZE_MIN}
                            aria-label={t('decreaseFontSize')}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <TextDecreaseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ minWidth: 32, textAlign: 'center', userSelect: 'none' }}
                          >
                            {fontSize}px
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => changeFontSize(FONT_SIZE_STEP)}
                            disabled={fontSize >= FONT_SIZE_MAX}
                            aria-label={t('increaseFontSize')}
                            sx={{ minWidth: 44, minHeight: 44 }}
                          >
                            <TextIncreaseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </Stack>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          borderLeft: 3,
                          borderColor: 'info.main',
                        }}
                        data-testid="transcript-youtube-comparison"
                      >
                        {renderTranscriptText(
                          youtubeComparison,
                          displayState.altTranscript,
                          fontSize,
                          'transcript-youtube-comparison-text',
                        )}
                      </Box>
                    </>
                  ) : onGetTranscript ? (
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<ArticleIcon />}
                      onClick={onGetTranscript}
                      data-testid="transcript-compare-btn"
                      sx={{ alignSelf: 'flex-start', color: 'text.secondary', minHeight: 44 }}
                    >
                      {t('compareWithYouTube')}
                    </Button>
                  ) : null;
                })()}
              </Stack>
            </Box>
          </Stack>
            );
          })()
        ) : captionState.status === 'loading' ? (
          /* ── Loading state ──────────────────────────────────────────────── */
          <Stack direction="row" alignItems="center" spacing={1.5} data-testid="transcript-loading">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('transcriptLoading')}
            </Typography>
          </Stack>
        ) : captionState.status === 'no-captions' ? (
          /* ── No-captions info state (expected — not an error) ────────── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Alert
              severity="info"
              data-testid="transcript-no-captions"
              sx={{ py: 0.5, width: '100%' }}
            >
              {t('transcriptNoCaptions')}
            </Alert>
            {/* Quota display — shown when quota data is available (Story 3.4) */}
            {quotaLoading ? (
              <CircularProgress size={14} data-testid="transcript-quota-loading" />
            ) : quotaData ? (
              <Box data-testid="transcript-quota-display" sx={{ mt: 1 }}>
                {quotaData.is_unlimited ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('translationQuotaUnlimited')}
                  </Typography>
                ) : quotaData.can_translate ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('translationQuotaRemaining', {
                      remaining: quotaData.auto_translations_limit - quotaData.auto_translations_used,
                      limit: quotaData.auto_translations_limit,
                    })}
                  </Typography>
                ) : (
                  <Stack spacing={1} alignItems="flex-start">
                    <Alert severity="warning" sx={{ py: 0.5, width: '100%' }} data-testid="transcript-quota-exhausted">
                      {t('translationQuotaExhausted')}
                    </Alert>
                    <Button
                      variant="contained"
                      size="small"
                      component="a"
                      href={ROUTES.SUBSCRIPTION}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quota-upgrade-btn"
                      sx={{ minHeight: 44 }}
                    >
                      {t('upgradeSubscription')}
                    </Button>
                  </Stack>
                )}
              </Box>
            ) : null}
            {editHook && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleWriteFromScratch}
                data-testid="transcript-write-btn"
                sx={{ alignSelf: 'flex-start', minHeight: 44 }}
              >
                {t('writeTranscript')}
              </Button>
            )}
          </Stack>
        ) : captionState.status === 'api-unavailable' ? (
          /* ── YouTube API temporarily unavailable (FR47) — NOT a subscription issue ── */
          <Stack spacing={1.5} alignItems="flex-start" data-testid="transcript-api-unavailable">
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('transcriptAPIUnavailable')}
            </Alert>
            {onGetTranscript && (
              <Button
                variant="outlined"
                size="small"
                onClick={onGetTranscript}
                data-testid="transcript-retry-btn"
                sx={{ minHeight: 44 }}
              >
                {t('retryTranscript')}
              </Button>
            )}
          </Stack>
        ) : captionState.status === 'quota-exceeded' ? (
          /* ── Quota-exceeded state — distinct from generic error ───────── */
          <Stack spacing={1.5} alignItems="flex-start" data-testid="transcript-quota-exceeded">
            <Alert severity="warning" sx={{ width: '100%' }}>
              {t('transcriptQuotaExceeded')}
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                component="a"
                href={ROUTES.SUBSCRIPTION}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quota-upgrade-btn"
                sx={{ minHeight: 44 }}
              >
                {t('upgradeSubscription')}
              </Button>
              {onGetTranscript && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={onGetTranscript}
                  data-testid="transcript-retry-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('retryTranscript')}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : captionState.status === 'error' ? (
          /* ── Error state ─────────────────────────────────────────────── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Alert
              severity="error"
              data-testid="transcript-error"
              sx={{ py: 0.5, width: '100%' }}
            >
              {t('transcriptFetchError', { message: captionState.message })}
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ArticleIcon />}
                onClick={onGetTranscript}
                data-testid="get-transcript-btn"
                sx={{ minHeight: 44 }}
              >
                {t('getTranscript')}
              </Button>
              {editHook && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleWriteFromScratch}
                  data-testid="transcript-write-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('writeTranscript')}
                </Button>
              )}
            </Stack>
          </Stack>
        ) : (
          /* ── Idle state: placeholder + Get Transcript button ─────────── */
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography variant="body1" color="text.secondary">
              {t('transcriptPlaceholder')}
            </Typography>
            <Stack direction="row" spacing={1}>
              {editHook && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={handleWriteFromScratch}
                  data-testid="transcript-write-btn"
                  sx={{ minHeight: 44 }}
                >
                  {t('writeTranscript')}
                </Button>
              )}
              <Button
                variant={editHook ? 'outlined' : 'contained'}
                size="small"
                startIcon={<ArticleIcon />}
                onClick={onGetTranscript}
                data-testid="get-transcript-btn"
                sx={{ minHeight: 44 }}
              >
                {t('getTranscript')}
              </Button>
            </Stack>
          </Stack>
            )}
            <Collapse in={explainOpen} unmountOnExit={false}>
              <ExplanationPanel
                isLoading={explainLoading}
                explanation={explanation}
                error={explainError}
                onDismiss={dismissExplain}
              />
            </Collapse>
            {/* Save + Explain row — constrained to left column width to match transcript area */}
            {(() => {
              const transcriptText = isEditing
                ? (editHook?.editText ?? '')
                : captionState.status === 'success'
                  ? captionState.transcript
                  : '';
              return (
                <Box sx={studyEditorResponsiveLayoutSx}>
                  <Stack direction="row" alignItems="center" sx={{ ...studyEditorResponsiveColumnSx, mt: 1 }}>
                    {editHook && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => editHook.saveNow()}
                        disabled={!editHook.hasUnsavedChanges || editHook.saveState === 'saving'}
                        data-testid="transcript-save-btn"
                        sx={{ minHeight: 44, mr: 1 }}
                      >
                        {t('saveTranscript')}
                      </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => explain(transcriptText)}
                      disabled={explainLoading || !transcriptText.trim()}
                      sx={{ minHeight: 44 }}
                    >
                      {explanation !== null && explainOpen ? 'Regenerate' : 'Explain'}
                    </Button>
                  </Stack>
                  <Box sx={studyEditorResponsiveColumnSx} />
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* Story 4.1: Vocabulary save popover — anchored near the selected text (Task 2.3, 2.5) */}
      <Popover
        open={savePopoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition}
        onClose={handlePopoverClose}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        data-testid="vocab-save-popover"
      >
        <Box sx={{ p: 2, minWidth: 220, maxWidth: 420, width: 'max-content' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }} data-testid="vocab-selected-word">
            {selectedWord}
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder={t('vocabTranslationPlaceholder')}
            value={manualTranslation}
            onChange={(e) => setManualTranslation(e.target.value)}
            sx={{ mb: 1 }}
            data-testid="vocab-translation-input"
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => {
                navigator.clipboard.writeText(selectedWord).catch(() => {});
                handlePopoverClose();
              }}
              sx={{ minHeight: 44 }}
              data-testid="vocab-copy-btn"
            >
              {t('copyWord')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleTranslateSelectedWord}
              disabled={
                translateText.isPending ||
                !selectedWord ||
                !projectLanguage ||
                !userLanguage
              }
              sx={{ minHeight: 44, display: 'grid' }}
              data-testid="vocab-translate-btn"
            >
              {/* Both labels occupy the same grid cell; the longer one (invisible when
                  inactive) reserves the button's width so it doesn't resize on click. */}
              <Box
                component="span"
                sx={{ gridArea: '1 / 1', visibility: translateText.isPending ? 'hidden' : 'visible' }}
              >
                {t('translateWord')}
              </Box>
              <Box
                component="span"
                sx={{ gridArea: '1 / 1', visibility: translateText.isPending ? 'visible' : 'hidden' }}
              >
                {t('translatingWord')}
              </Box>
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                if (!segment || projectId == null) return;
                addWordMutation.mutate({
                  word: selectedWord,
                  context: captionState.status === 'success' ? captionState.transcript : '',
                  segmentId: segment.id,
                  projectId,
                  sourceType: 'transcript',
                  translation: manualTranslation || undefined,
                });
              }}
              disabled={addWordMutation.isPending}
              sx={{ minHeight: 44 }}
              data-testid="vocab-save-btn"
            >
              {t('saveVocab')}
            </Button>
            <IconButton
              size="small"
              onClick={handlePopoverClose}
              aria-label="close"
              data-testid="vocab-popover-close"
              sx={{ minWidth: 44, minHeight: 44 }}
            >
              ×
            </IconButton>
          </Stack>
        </Box>
      </Popover>
      {segment && (
        <NoteModal
          open={isNoteModalOpen}
          onClose={() => setIsNoteModalOpen(false)}
          segmentId={segment.id}
          noteType="transcript"
          sourceText={noteSourceText}
          hasExistingNote={segment.hasTranscriptNote ?? false}
        />
      )}
    </Box>
  );
};
